import { forwardRef } from 'react';
import { 
  Box, 
  Typography, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Paper,
  Grid
} from '@mui/material';

// Props: appointment, orders, hospitalInfo, consultations, questions
interface CaseStudyPrintProps {
  appointment: any;
  orders: any[];
  hospitalInfo: {
    logoUrl: string;
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
  consultations?: any[];
  questions?: any[];
}

const CaseStudyPrint = forwardRef<HTMLDivElement, CaseStudyPrintProps>(
  ({ appointment, orders, hospitalInfo, consultations = [], questions = [] }, ref) => {
    // Calculate totals
    const validOrders = orders.filter(order => {
      const financialStatus = order.financialStatus;
      return financialStatus !== 'CANCELLED' && financialStatus !== 'REFUNDED';
    });

    // Group orders by item group
    const groupedOrders = validOrders.reduce((groups: { [key: string]: any[] }, order) => {
      const groupName = order.item?.itemGroup || 'OTHERS';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(order);
      return groups;
    }, {});

    // Define proper order for item groups
    const groupOrder = ['CONSULTATION', 'INVESTIGATION', 'THERAPIES', 'PHARMACY', 'CONSUMABLE', 'STATIONERY', 'OTHERS'];
    
    // Sort groups according to defined order
    const sortedGroupNames = Object.keys(groupedOrders).sort((a, b) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      return a.localeCompare(b);
    });

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const formatTime = (dateString: string) => {
      return new Date(dateString).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    return (
      <Paper ref={ref} sx={{ p: 3, minHeight: '297mm', width: '210mm', margin: 'auto' }}>
        {/* Header - Similar to Invoice */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          mb: 3,
          pb: 2,
          borderBottom: '2px solid #f0f0f0',
          backgroundColor: '#fafafa',
          p: 2,
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          {hospitalInfo.logoUrl && (
            <Box sx={{ mr: 3, flexShrink: 0 }}>
              <img 
                src={hospitalInfo.logoUrl} 
                alt="Hospital Logo" 
                style={{ height: '80px', width: 'auto' }}
              />
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 1, color: '#333' }}>
              {hospitalInfo.name}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5, lineHeight: 1.4 }}>
              {hospitalInfo.address}
            </Typography>
            {hospitalInfo.phone && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Phone: {hospitalInfo.phone}
              </Typography>
            )}
            {hospitalInfo.email && (
              <Typography variant="body2">
                Email: {hospitalInfo.email}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: '#333' }}>
              Case Study Report
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Date: {formatDate(new Date().toISOString())}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {appointment.patient?.fullName || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Doctor: Dr. {appointment.doctor?.firstName} {appointment.doctor?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Appointment No: {appointment.appointmentNumber || appointment.id}
            </Typography>
          </Box>
        </Box>



        {/* Patient Questions & Responses */}
        {questions && questions.length > 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Patient Questions & Responses
              </Typography>
              <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Question</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem', width: '100px' }}>Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((question, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {question.question}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                        {question.answer || 'No'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Additional Conditions */}
              {consultations && consultations.length > 0 && consultations[0].additionalConditions && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    Additional Conditions (not listed above):
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {consultations[0].additionalConditions}
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Consultation Notes */}
        {consultations && consultations.length > 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Consultation Notes
              </Typography>
              {consultations.map((consultation, index) => (
                <Box key={index} sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.9rem' }}>
                    Session {index + 1} - {formatDate(consultation.createdAt)}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {consultation.chiefComplaint && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>Chief Complaint:</strong> {consultation.chiefComplaint}
                        </Typography>
                      )}
                      {consultation.history && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>History:</strong> {consultation.history}
                        </Typography>
                      )}
                      {consultation.examination && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>Examination:</strong> {consultation.examination}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {consultation.diagnosis && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>Diagnosis:</strong> {consultation.diagnosis}
                        </Typography>
                      )}
                      {consultation.treatmentPlan && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>Treatment Plan:</strong> {consultation.treatmentPlan}
                        </Typography>
                      )}
                      {consultation.notes && (
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          <strong>Notes:</strong> {consultation.notes}
                        </Typography>
                      )}
                      {consultation.additionalConditions && (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          <strong>Additional Conditions:</strong> {consultation.additionalConditions}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Orders/Services Provided */}
        {sortedGroupNames.length > 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Services & Orders
              </Typography>
              {sortedGroupNames.map((groupName) => (
                <Box key={groupName} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
                    {groupName}
                  </Typography>
                  <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Item/Service</strong></TableCell>
                        <TableCell align="center"><strong>Qty</strong></TableCell>
                        <TableCell><strong>Dosage</strong></TableCell>
                        <TableCell><strong>Instructions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedOrders[groupName].map((order: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {order.item?.itemName || 'N/A'}
                            </Typography>
                            {order.item?.description && (
                              <Typography variant="caption" color="text.secondary">
                                {order.item.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{order.quantity || 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.dosage || order.item?.dosage || 'As prescribed'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.instructions || order.item?.instructions || 'Take as directed by physician'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Financial Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Total Services:</strong> {validOrders.length}
              </Typography>
              <Typography variant="body2">
                <strong>Subtotal:</strong> ₹{validOrders.reduce((sum, order) => sum + (order.total || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>Total Discount:</strong> ₹{validOrders.reduce((sum, order) => sum + (order.discountValue || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Tax Amount:</strong> ₹{validOrders.reduce((sum, order) => sum + (order.taxAmount || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                <strong>Grand Total:</strong> ₹{validOrders.reduce((sum, order) => sum + (order.grandTotal || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>Paid Amount:</strong> ₹{validOrders.reduce((sum, order) => sum + (order.paidAmount || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: '2px solid #f0f0f0',
          backgroundColor: '#fafafa',
          p: 2,
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Generated on:</strong> {new Date().toLocaleDateString('en-GB')} at {new Date().toLocaleTimeString('en-GB')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This is a computer-generated case study report from {hospitalInfo.name}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Doctor's Signature</strong>
              </Typography>
              <Box sx={{ 
                borderBottom: '1px solid #333', 
                width: '200px', 
                height: '40px', 
                ml: 'auto',
                mb: 1
              }} />
              <Typography variant="caption" color="text.secondary">
                Dr. {appointment.doctor?.firstName} {appointment.doctor?.lastName}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    );
  }
);

CaseStudyPrint.displayName = 'CaseStudyPrint';

export default CaseStudyPrint;
